// ``GET /api/v1/config/public`` as a TanStack query.
//
// Unauthenticated (registration needs it before there is a session).
// Shared by every consumer — the registration form, the username
// panel and the "Open in Office" gate — so one fetch serves them all.
//
// A failed fetch is an *error* state, not cached data: the query
// retries and refetches on window focus, instead of the previous
// module-level cache that remembered ``null`` ("no editor") for the
// rest of the page's life after a single transient failure.

import { useQuery } from "@tanstack/react-query";

import type { PublicConfig } from "@/auth/flows";

export const publicConfigKey = ["config", "public"] as const;

export async function loadPublicConfig(): Promise<PublicConfig> {
  const res = await fetch("/api/v1/config/public");
  if (!res.ok) {
    throw new Error(`could not load public config (HTTP ${res.status})`);
  }
  return (await res.json()) as PublicConfig;
}

export function usePublicConfig() {
  return useQuery({
    queryKey: publicConfigKey,
    queryFn: loadPublicConfig,
    // Admin-set and rarely changes; window-focus refetch (global
    // default) still picks up a flipped env var without a reload.
    staleTime: 5 * 60_000,
  });
}

/** The server's minimum password length (12 until the config answers). */
export function usePasswordMinLength(): number {
  return usePublicConfig().data?.password_min_length ?? 12;
}
